import { TLImageAsset } from '@tldraw/tldraw'
import { ColorSet, ImageShape, SvgAsset } from './Types'
import QueryString from 'qs'

export const extractColors = (svg: string): Record<string, string> => {
  const colorRegex =
    /#([0-9a-fA-F]{3}){1,2}|rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\)|rgba\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*(?:0|1|0?\.\d+)\s*\)/g
  const set = new Set(svg.match(colorRegex))
  if (set.size === 0) {
    set.add('#000000')
  }
  return Object.fromEntries(set.entries())
}

export const createBlobUrlFromSvg = (svg: string) => {
  const byteArray = new Uint8Array(svg.length)
  for (let i = 0; i < svg.length; i++) {
    byteArray[i] = svg.charCodeAt(i)
  }

  const blob = new Blob([byteArray], { type: 'image/svg+xml' })
  return URL.createObjectURL(blob)
}

export function isSvgAsset(asset: TLImageAsset): asset is SvgAsset {
  return Boolean(asset.props.src) && asset.props.mimeType === 'image/svg+xml'
}

export async function getDataURIFromURL(url: string): Promise<string> {
  const response = await fetch(url)
  const blob = await response.blob()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

/**
 * When an image is cropped we need to translate the image to show the portion withing the cropped
 * area. We do this by translating the image by the negative of the top left corner of the crop
 * area.
 *
 * @param shape - Shape The image shape for which to get the container style
 * @returns - Styles to apply to the image container
 */
export function getContainerStyle(shape: ImageShape) {
  const crop = shape.props.crop
  const topLeft = crop?.topLeft
  if (!topLeft) {
    return {
      width: shape.props.w,
      height: shape.props.h,
    }
  }

  const w = (1 / (crop.bottomRight.x - crop.topLeft.x)) * shape.props.w
  const h = (1 / (crop.bottomRight.y - crop.topLeft.y)) * shape.props.h

  const offsetX = -topLeft.x * w
  const offsetY = -topLeft.y * h
  return {
    transform: `translate(${offsetX}px, ${offsetY}px)`,
    width: w,
    height: h,
  }
}

export const noCacheUrl = (url: string) => {
  const [path, queryString] = url.split('?')
  const newUrl = `${path}?${QueryString.stringify({
    ...QueryString.parse(queryString),
    'no-cache': true,
  })}`
  return newUrl
}

const SHAPE_SELECTOR = 'path, circle, rect, ellipse, polygon, line, polyline'

/**
 * When the color key is missing from the SVG (e.g. stroke icons that rely on
 * the browser default black stroke and fill="none"), apply the replacement to
 * fill and/or stroke on shape nodes so line-style graphics recolor correctly.
 */
const applySingleColorFallback = (svgStr: string, color: string) => {
  const parser = new DOMParser()
  const svg = parser.parseFromString(svgStr, 'image/svg+xml')
  const root = svg.querySelector('svg')
  if (!root) return svgStr

  root.setAttribute('fill', color)

  svg.querySelectorAll(SHAPE_SELECTOR).forEach((el: Element) => {
    const fill = el.getAttribute('fill')
    const tag = el.tagName.toLowerCase()
    const isStrokeOnly = tag === 'line' || tag === 'polyline' || fill === 'none'

    if (isStrokeOnly) {
      el.setAttribute('stroke', color)
      return
    }

    if (!fill || fill === 'black' || fill === 'currentColor') {
      el.setAttribute('fill', color)
    }

    const stroke = el.getAttribute('stroke')
    if (stroke && (stroke === 'black' || stroke === 'currentColor' || stroke === '#000000' || stroke === '#000')) {
      el.setAttribute('stroke', color)
    }
  })

  // Line-style icons often set fill="none" + stroke on a parent <g>
  svg.querySelectorAll('g').forEach((el: Element) => {
    if (el.getAttribute('fill') === 'none') {
      el.setAttribute('stroke', color)
    }
  })

  return new XMLSerializer().serializeToString(svg)
}

export const replaceSvgColors = (svgStr: string, colorSet: ColorSet) => {
  if (svgStr.includes('data:')) {
    return svgStr
  }

  const pairs = Object.entries(colorSet)

  // There is only 1 color and it is not found in the svg, so we apply a fill/stroke fallback
  if (pairs.length === 1 && !svgStr.includes(pairs[0][0])) {
    return applySingleColorFallback(svgStr, pairs[0][1])
  }

  const regex = new RegExp(Object.keys(colorSet).join('|'), 'g')
  return svgStr.replace(regex, (match) => colorSet[match])
}
